import React from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency } from '../../../shared/utils';

export interface ConfirmOperationModalProps {
  isOpen: boolean;
  type: 'success' | 'danger';
  title: string;
  description: React.ReactNode;
  amount: number;
  action: 'approve' | 'reject' | null;
  reasonInput: string;
  setReasonInput: (value: string) => void;
  isProcessing: boolean;
  onClose: () => void;
  onExecute: () => void;
}

export const ConfirmOperationModal: React.FC<ConfirmOperationModalProps> = ({
  isOpen,
  title,
  description,
  amount,
  action,
  reasonInput,
  setReasonInput,
  isProcessing,
  onClose,
  onExecute
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-[#212121]">{title}</h2>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>

        {/* Amount pill */}
        <div className="mx-6 mb-4 flex items-center justify-between bg-gray-100 rounded-lg px-4 py-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Monto de la solicitud</span>
          <span className="text-base font-bold text-[#212121]">{formatCurrency(amount)}</span>
        </div>

        {/* Rejection reason textarea */}
        {action === 'reject' && (
          <div className="mx-6 mb-4">
            <label className="block text-sm font-semibold text-[#212121] mb-2">
              Descripción <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              placeholder="Describí brevemente el motivo del rechazo"
              className="w-full px-3 py-2.5 border-2 border-[#006837] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#006837]/30 bg-white text-[#212121] resize-none"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {/* Footer buttons */}
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onExecute}
            disabled={isProcessing}
            className={`px-5 py-2.5 text-sm font-bold text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50 ${
              action === 'approve'
                ? 'bg-[#006837] hover:bg-[#005a2f]'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isProcessing && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isProcessing
              ? (action === 'approve' ? 'Aprobando...' : 'Rechazando...')
              : (action === 'approve' ? 'Aprobar' : 'Rechazar')
            }
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
